import { Injectable } from '@angular/core';
import Parse from 'parse';
import {
  AssetTypeTopology,
  CaseTopology,
  ObjectTopology,
  SampleTopology,
  MethodTypeTopology,
  WorkspaceTopology,
} from '@telespot/infrastructure/parse';

export interface CaseNavigationReference {
  caseId: string;
  sampleId: string;
}

export interface Info {
  id: string;
  name: string;
}

export interface SampleListItem extends Info {
  /** Name of the sample's method type, so the dropdown can distinguish samples
   *  of different method types within the same case. */
  methodType?: string;
  /** Whether the sample's method-type asset type is video (vs image). Lets the
   *  caller route to the right viewer (videos vs images) on navigation. */
  isVideo?: boolean;
}

export interface NavigationData {
  previousCase: CaseNavigationReference | null;
  nextCase: CaseNavigationReference | null;
  previousSample: string | null;
  nextSample: string | null;
  currentSample: Info;
  currentCase: Info;
  currentWorkspace: Info;
}

@Injectable({
  providedIn: 'root',
})
export class MicroService {
  async fetchNavigationReferences(sampleId: string, onlyCreatedById?: string): Promise<NavigationData> {
    const Sample = Parse.Object.extend(SampleTopology.TABLE);
    const Case = Parse.Object.extend(CaseTopology.TABLE);

    const sample = await Sample.createWithoutData(sampleId).fetchWithInclude([
      `${SampleTopology.METHOD_TYPE}.${MethodTypeTopology.ASSET_TYPE}`,
      `${SampleTopology.CASE}.${CaseTopology.WORKSPACE}`,
      `${SampleTopology.CASE}.${CaseTopology.CASE_TYPE}`,
      `${SampleTopology.METHOD_TYPE}.${MethodTypeTopology.ANALYSIS_TYPES}`,
    ]);

    // When `onlyCreatedById` is set (caller is a pure-technician), restrict
    // prev/next case to cases the user created. Mirrors the rule enforced in
    // ts-workspace-cases so the in-viewer breadcrumb cannot jump to a case
    // that the workspace list would have hidden.
    const restrictByCreator = onlyCreatedById
      ? (q: Parse.Query) => q.equalTo('createdBy', Parse.User.createWithoutData(onlyCreatedById))
      : (q: Parse.Query) => q;

    const nextCaseQuery = restrictByCreator(
      new Parse.Query(Case)
        .greaterThan(ObjectTopology.CREATED_AT, new Date(sample.case.createdAt))
        .ascending(ObjectTopology.CREATED_AT)
        .equalTo(CaseTopology.WORKSPACE, sample.case.workspace)
    );

    const previousCaseQuery = restrictByCreator(
      new Parse.Query(Case)
        .lessThan(ObjectTopology.CREATED_AT, new Date(sample.case.createdAt))
        .descending(ObjectTopology.CREATED_AT)
        .equalTo(CaseTopology.WORKSPACE, sample.case.workspace)
    );

    const [nextCase, previousCase] = await Promise.all([
      nextCaseQuery.first().catch(() => null),
      previousCaseQuery.first().catch(() => null),
    ]);

    const nextSampleQuery = new Parse.Query(Sample)
      .greaterThan(ObjectTopology.CREATED_AT, new Date(sample.createdAt))
      .ascending(ObjectTopology.CREATED_AT)
      .equalTo(SampleTopology.CASE, sample.case);

    const previousSampleQuery = new Parse.Query(Sample)
      .lessThan(ObjectTopology.CREATED_AT, new Date(sample.createdAt))
      .descending(ObjectTopology.CREATED_AT)
      .equalTo(SampleTopology.CASE, sample.case);

    const [nextSample, previousSample] = await Promise.all([
      nextSampleQuery.first().catch(() => null),
      previousSampleQuery.first().catch(() => null),
    ]);

    let nextCaseSample = null;
    let previousCaseSample = null;

    if (nextCase?.id) {
      const nextCaseSampleQuery = new Parse.Query(Sample)
        .equalTo(SampleTopology.CASE, Case.createWithoutData(nextCase.id))
        .exists(SampleTopology.NUM_ASSETS);
      nextCaseSample = await nextCaseSampleQuery.first().catch(() => null);
    }

    if (previousCase?.id) {
      const previousCaseSampleQuery = new Parse.Query(Sample)
        .equalTo(SampleTopology.CASE, Case.createWithoutData(previousCase.id))
        .exists(SampleTopology.NUM_ASSETS);
      previousCaseSample = await previousCaseSampleQuery.first().catch(() => null);
    }

    // The workspace comes through a 2-level include (case -> workspace). Under
    // some cache states the nested pointer resolves without its attributes, so
    // `name` arrives empty intermittently on reload. Fetch it explicitly when
    // that happens so the breadcrumb always has a name.
    let workspace = sample.case.workspace;
    if (workspace && !workspace.get(WorkspaceTopology.NAME)) {
      workspace = await workspace.fetch().catch(() => workspace);
    }

    const result: NavigationData = {
      previousCase:
        previousCase && previousCaseSample ? { caseId: previousCase.id, sampleId: previousCaseSample.id } : null,
      nextCase: nextCase && nextCaseSample ? { caseId: nextCase.id, sampleId: nextCaseSample.id } : null,
      previousSample: previousSample?.id || null,
      nextSample: nextSample?.id || null,
      currentCase: { id: sample.case.id, name: sample.case.name },
      currentSample: { id: sample.id, name: sample.name },
      currentWorkspace: workspace ? { id: workspace.id, name: workspace.get(WorkspaceTopology.NAME) } : null,
    };

    return result;
  }

  /** Workspaces that belong to the SAME organization as the given workspace.
   *  Avoids mixing workspaces from every org the user is affiliated with.
   *  Parse `beforeFind` hooks still apply on top (access scoping). */
  async listWorkspacesInSameOrg(currentWorkspaceId: string): Promise<Info[]> {
    const Workspace = Parse.Object.extend(WorkspaceTopology.TABLE);

    const current = await Workspace.createWithoutData(currentWorkspaceId)
      .fetch()
      .catch(() => null);
    const organization = current?.get(WorkspaceTopology.ORGANIZATION);
    if (!organization) return [];

    const results = await new Parse.Query(Workspace)
      .equalTo(WorkspaceTopology.ORGANIZATION, organization)
      .ascending(WorkspaceTopology.NAME)
      .limit(500)
      .find();

    return results.map((w) => ({ id: w.id, name: w.get(WorkspaceTopology.NAME) }));
  }

  /**
   * Cases inside a workspace, oldest first (matches sequential navigation).
   * When `onlyCreatedById` is set, restricts the result to cases created by
   * that user — used to mirror the pure-technician restriction enforced by
   * `ts-workspace-cases`.
   */
  async listCasesInWorkspace(workspaceId: string, onlyCreatedById?: string): Promise<Info[]> {
    const Workspace = Parse.Object.extend(WorkspaceTopology.TABLE);
    const Case = Parse.Object.extend(CaseTopology.TABLE);

    const query = new Parse.Query(Case)
      .equalTo(CaseTopology.WORKSPACE, Workspace.createWithoutData(workspaceId))
      .ascending(ObjectTopology.CREATED_AT)
      .limit(1000);

    if (onlyCreatedById) {
      query.equalTo('createdBy', Parse.User.createWithoutData(onlyCreatedById));
    }

    const results = await query.find();

    return results.map((c) => ({ id: c.id, name: c.get(CaseTopology.NAME) }));
  }

  /** Samples inside a case that actually have at least one asset (samples with
   *  no assets aren't viewable, so they're excluded from the picker). Includes
   *  the method type name so the dropdown can show it. */
  async listSamplesInCase(caseId: string): Promise<SampleListItem[]> {
    const Case = Parse.Object.extend(CaseTopology.TABLE);
    const Sample = Parse.Object.extend(SampleTopology.TABLE);

    const results = await new Parse.Query(Sample)
      .equalTo(SampleTopology.CASE, Case.createWithoutData(caseId))
      .greaterThan(SampleTopology.NUM_ASSETS, 0)
      .include(SampleTopology.METHOD_TYPE)
      .include(`${SampleTopology.METHOD_TYPE}.${MethodTypeTopology.ASSET_TYPE}`)
      .ascending(ObjectTopology.CREATED_AT)
      .limit(100)
      .find();

    return results.map((s) => {
      const methodType = s.get(SampleTopology.METHOD_TYPE);
      return {
        id: s.id,
        name: s.get(SampleTopology.NAME),
        methodType: methodType?.get(MethodTypeTopology.NAME),
        isVideo: methodType?.get(MethodTypeTopology.ASSET_TYPE)?.get(AssetTypeTopology.TYPE) === 'video',
      };
    });
  }

}
